"use client";

import { useState } from "react";
import { Check, X, ChevronDown, ChevronUp } from "@/components/icons";
import { cn } from "@/lib/utils";
import type { FeatureCategory } from "../data";
import { unifiedFeatures } from "../data";
import { CATALOGUE_TIERS } from "@shared/subscriptions/plan-content";
import { TIER_DISPLAY_NAMES } from "@shared/subscriptions/tiers";

function FeatureValue({ value }: { value: boolean | string }) {
  if (typeof value === "boolean") {
    return value ? (
      <Check className="h-4 w-4 text-success-strong" />
    ) : (
      <X className="h-4 w-4 text-muted-foreground" />
    );
  }
  return <span className="tabular-nums text-xs">{value}</span>;
}

interface CategoryToggleProps {
  name: string;
  categoryId: string;
  isExpanded: boolean;
  onToggle: () => void;
}

function CategoryToggle({
  name,
  categoryId,
  isExpanded,
  onToggle,
}: CategoryToggleProps) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full min-h-11 items-center justify-between bg-muted px-4 py-2 text-left hover:bg-muted/80 md:hidden"
      aria-expanded={isExpanded}
      aria-controls={`category-${categoryId}`}
    >
      <span className="text-xs font-medium text-muted-foreground">
        {name}
      </span>
      {isExpanded ? (
        <ChevronUp className="h-4 w-4 text-muted-foreground" />
      ) : (
        <ChevronDown className="h-4 w-4 text-muted-foreground" />
      )}
    </button>
  );
}

function CategorySection({
  category,
  isExpanded,
  onToggle,
}: {
  category: FeatureCategory;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  const categoryId = category.name.toLowerCase().replace(/\s+/g, "-");

  return (
    <div className="border-b border-border last:border-b-0">
      <CategoryToggle
        name={category.name}
        categoryId={categoryId}
        isExpanded={isExpanded}
        onToggle={onToggle}
      />

      <div className="hidden bg-muted px-4 py-2 md:block">
        <span className="text-xs font-medium text-muted-foreground">
          {category.name}
        </span>
      </div>

      <div
        id={`category-${categoryId}`}
        className={cn("md:block", isExpanded ? "block" : "hidden")}
      >
        {category.features.map((feature, index) => (
          <div
            key={index}
            className="grid grid-cols-4 items-center border-t border-border px-4 py-2 text-sm"
          >
            <div className="text-muted-foreground">{feature.name}</div>
            {CATALOGUE_TIERS.map((tier) => (
              <div key={tier} className="flex justify-center">
                <FeatureValue value={feature[tier]} />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function TierHeaderRow() {
  return (
    <div className="grid grid-cols-4 items-center border-b border-border px-4 py-3">
      <div className="text-xs font-medium text-muted-foreground">Feature</div>
      {CATALOGUE_TIERS.map((tier) => (
        <div key={tier} className="text-center">
          <span
            className={cn(
              "text-xs font-medium",
              tier === "pro" ? "text-action" : "text-muted-foreground",
            )}
          >
            {TIER_DISPLAY_NAMES[tier]}
          </span>
        </div>
      ))}
    </div>
  );
}

export function FeatureTable() {
  const categories = unifiedFeatures;
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set([categories[0]?.name]),
  );

  const toggleCategory = (name: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  };


  return (
    <div className="border border-border bg-background">
      <div className="flex items-center gap-2 border-b border-border bg-muted px-4 py-2">
        <span className="text-xs font-medium text-muted-foreground">
          Feature Comparison
        </span>
      </div>

      <TierHeaderRow />

      {categories.map((category) => (
        <CategorySection
          key={category.name}
          category={category}
          isExpanded={expandedCategories.has(category.name)}
          onToggle={() => toggleCategory(category.name)}
        />
      ))}
    </div>
  );
}
